import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QuickOrderDesktopTable from '../Components/QuickOrderPage/QuickOrderDesktopTable';
import QuickOrderMobileTable from '../Components/QuickOrderPage/QuickOrderMobileTable';
import useIsMobile from '../Hooks/useIsMobile';
import { receive as cartReceive } from '../Actions/Cart.action';
import {
  queryArticleNumber,
  queryName,
  setIsLoading,
  onError,
} from '../Actions/QuickOrder.action';
import { addMultiple } from '../Services/Cart.service';
import QuickOrderHeader from '../Components/QuickOrderPage/QuickOrderHeader';
import QuickOrderFooter from '../Components/QuickOrderPage/QuickOrderFooter';
import { get, post } from '../Services/http';
import { translate } from '../Services/translation';

const DEFAULT_NUM_ROWS = 2;
const EMPTY_ROW = {
  articleNumber: '',
  quantity: 1,
  quantityInStock: null,
  name: '',
  price: null,
  currency: null,
  inStock: false,
  formattedTotalPrice: '',
  formattedPrice: '',
  error: false,
};

const formattedTotalPrice = 'formattedTotalPrice';

const DEFAULT_NOTIFICATION = {
  text: '',
  type: '',
};

const QuickOrderContainer = () => {
  let dispatch = useDispatch();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);

  const quickOrder = useSelector((state) => state.quickOrder);
  const queryResult = quickOrder.queryResult;
  const [showNameAutoComplete, setShowNameAutoComplete] = useState(false);
  const [notification, setNotification] = useState(DEFAULT_NOTIFICATION);

  const clearRows = () => {
    setRows([]);
    var tempRows = [];
    for (var rowIdx = 0; rowIdx < DEFAULT_NUM_ROWS; rowIdx++) {
      tempRows.push(getEmptyRow());
    }
    setRows(tempRows);
    addNotification({ ...DEFAULT_NOTIFICATION });
  };

  useEffect(() => {
    clearRows();
  }, []);

  const getRowsCopy = () => Object.assign([], rows);

  const getEmptyRow = () => Object.assign({}, EMPTY_ROW);

  const addRow = () => setRows((rows) => [...rows, getEmptyRow()]);

  const clearRow = (rowIndex) => {
    var rowsCopy = getRowsCopy();
    rowsCopy[rowIndex] = getEmptyRow();
    setRows(rowsCopy);
    addNotification({ ...DEFAULT_NOTIFICATION });
  };

  const getCompleteRows = () => {
    return rows.filter((item) => item.articleNumber);
  };

  const onAutoCompleteItemSelected = (item, rowIndex) => {
    if (item.availableForQuickOrder) {
      selectItem(item, rowIndex);
    }
    addNotification({ ...DEFAULT_NOTIFICATION });
  };

  const addRowToEnd = (rowsCopy) => {
    var lastRow = rowsCopy.slice(-1);
    if (lastRow[0].name !== '' && lastRow[0].articleNumber !== '') {
      var emptyRowIndex = rowsCopy.length;
      rowsCopy[emptyRowIndex] = getEmptyRow();
    }
  };

  const selectItem = (item, rowIndex) => {
    var rowsCopy = getRowsCopy();
    dispatch(setIsLoading(true));
    get('/api/quickorder/' + item.articleNumber)
      .then((res) => res.json())
      .then((rowData) => {
        rowData.quantity = 1;
        rowsCopy[rowIndex] = rowData;
        addRowToEnd(rowsCopy);
        setRows(rowsCopy);
        dispatch(setIsLoading(false));
        setShowNameAutoComplete(false);
        dispatch(queryArticleNumber(null));
        dispatch(queryName(null));
      })
      .catch((err) => {
        addNotification({ type: 'error', text: err });
        dispatch(setIsLoading(false));
      });
  };

  const getCalculatedTotalPrice = (product, quantity) => {
    if (product == null) return;
    var total = product.totalPrice;

    if (product.tierPrices.length > 0) {
      product.tierPrices.forEach((row) => {
        if (
          (quantity >= row.minimumQuantity &&
            quantity <= row.nextMinimumQuantity) ||
          (quantity >= row.minimumQuantity && row.nextMinimumQuantity == null)
        ) {
          // We are at the right tierprice range
          total = calculateTotalRowPrice(row.price, quantity);
          return false;
        }
      });
    } else {
      total = calculateTotalRowPrice(product.price?.price, quantity);
    }
    return total;
  };

  const calculateTotalRowPrice = (price, quantity) => {
    if (isNaN(price)) {
      price = Number(price);
    }
    return (price * quantity).toString();
  };

  const formatPrice = (currencySymbol, price) => {
    return `${price} ${currencySymbol}`;
  };

  const onQuantityInputChange = (rowIndex, value) => {
    var rowsCopy = getRowsCopy();
    if (value <= 0) {
      rowsCopy[rowIndex] = getEmptyRow();
      setRows(rowsCopy);
      return;
    } else if (value != '') {
      var currencySymbol =
        rowsCopy[rowIndex].currency?.symbol ??
        rowsCopy[rowIndex].totalPrice.match(/(\d+)/);
      var formattedRowPrice = formatPrice(
        currencySymbol,
        getCalculatedTotalPrice(rowsCopy[rowIndex], value)
      );
      rowsCopy[rowIndex][formattedTotalPrice] = formattedRowPrice;
    }
    rowsCopy[rowIndex]['quantity'] = value;
    setRows(rowsCopy);
  };

  const onNameInputChange = (rowIndex, key, value) => {
    var rowsCopy = getRowsCopy();
    rowsCopy[rowIndex][key] = value;
    setRows(rowsCopy);
    dispatch(queryName(value));
    if (value == '') {
      setShowNameAutoComplete(false);
    } else {
      setShowNameAutoComplete(true);
    }
  };

  const onNoteInputChange = (rowIndex, key, value) => {
    var rowsCopy = getRowsCopy();
    rowsCopy[rowIndex][key] = value;
    setRows(rowsCopy);
    addNotification({ ...DEFAULT_NOTIFICATION });
  };

  const onArticleNumblerChange = (rowIndex, key, value) => {
    var rowsCopy = getRowsCopy();
    rowsCopy[rowIndex][key] = value;
    setRows(rowsCopy);
    addNotification({ ...DEFAULT_NOTIFICATION });
  };

  const onArticleNumblerBlur = (rowIndex, value) => {
    if (value && value.trim() != '') {
      addNotification({ ...DEFAULT_NOTIFICATION });
      dispatch(queryArticleNumber(value)).then((res) => {
        var rowsCopy = getRowsCopy();
        if (res !== undefined) {
          rowsCopy[rowIndex] = res;
        } else {
          rowsCopy[rowIndex] = getEmptyRow();
          addNotification({
            type: 'error',
            text: `${translate('quickorder.article-not-found')} ${value}`,
          });
          rowsCopy[rowIndex]['error'] = true;
        }
        addRowToEnd(rowsCopy);
        setRows(rowsCopy);
      });
      return;
    }
  };

  const onTabClick = (rowIndex) => {
    if (
      queryResult &&
      queryResult.articles &&
      queryResult.articles.length > 0
    ) {
      var item = queryResult.articles[0];
      if (item) {
        selectItem(item, rowIndex);
      }
    }
  };

  const checkIfCanAddToCart = () => {
    var canAdd = false;
    rows.forEach((row) => {
      if (row.articleNumber != '' && row.name != '') canAdd = true;
      return false;
    });
    return canAdd;
  };

  const addNotification = ({ type, text }) => {
    if (type == '' && text == '') {
      setNotification(DEFAULT_NOTIFICATION);
    } else if (type && type != '' && text && text != '') {
      setNotification({ type: type, text: text });
    }
  };

  return (
    <div>
      <QuickOrderHeader
        defaultNumRows={DEFAULT_NUM_ROWS}
        setRows={setRows}
        getEmptyRow={getEmptyRow}
        formatPrice={formatPrice}
        getCalculatedTotalPrice={getCalculatedTotalPrice}
        addRowToEnd={addRowToEnd}
        addNotification={addNotification}
        notification={notification}
        defaultNotification={DEFAULT_NOTIFICATION}
      />

      {isMobile ? (
        <QuickOrderMobileTable
          {...quickOrder}
          rows={rows}
          showNameAutoComplete={showNameAutoComplete}
          onTabClick={onTabClick}
          onAutoCompleteItemSelected={onAutoCompleteItemSelected}
          clearRow={clearRow}
          onArticleNumblerBlur={onArticleNumblerBlur}
          onQuantityInputChange={onQuantityInputChange}
          onNameInputChange={onNameInputChange}
          onNoteInputChange={onNoteInputChange}
          onArticleNumblerChange={onArticleNumblerChange}
          addNotification={addNotification}
          defaultNotification={DEFAULT_NOTIFICATION}
        />
      ) : (
        <QuickOrderDesktopTable
          {...quickOrder}
          rows={rows}
          showNameAutoComplete={showNameAutoComplete}
          onTabClick={onTabClick}
          onAutoCompleteItemSelected={onAutoCompleteItemSelected}
          clearRow={clearRow}
          onArticleNumblerBlur={onArticleNumblerBlur}
          onQuantityInputChange={onQuantityInputChange}
          onNameInputChange={onNameInputChange}
          onNoteInputChange={onNoteInputChange}
          onArticleNumblerChange={onArticleNumblerChange}
          addNotification={addNotification}
          defaultNotification={DEFAULT_NOTIFICATION}
        />
      )}

      <QuickOrderFooter
        getCompleteRows={getCompleteRows}
        addMultipleToCart={async (products) => {
          try {
            const cart = await addMultiple(products);
            dispatch(cartReceive(cart));
          } catch (err) {
            dispatch(onError(err));
          }
        }}
        clearRows={clearRows}
        canAddToCart={checkIfCanAddToCart()}
        addRow={addRow}
        addNotification={addNotification}
        defaultNotification={DEFAULT_NOTIFICATION}
      />
    </div>
  );
};

export default QuickOrderContainer;
